import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import {
  Parameters,
  PatrimonyRepartition,
  PledgeRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { DistributionAssets } from '@app/forms/subscription/definition/interface/subscription';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import * as _ from 'lodash';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  constructor(
    protected formConstructor: PrFormConstructor,
    protected subscriptionFacade: SubscriptionFacadeService,
  ) {
    super(formConstructor);
  }
  generate(entity: PledgeRead) {
    const parameters: Parameters = {
      ...entity?.parameters,
    };
    let options: AbstractControlOptions = {};
    const formConfig: FormConfig<PatrimonyRepartition> = {
      subjectToIfi: [
        parameters?.subjectToIfi || false,
        [this.prFormService.RefreshEveryValidators('ifi'), Validators.required],
      ],
      ifi: [
        parameters?.ifi || null,
        [
          this.prFormService.AddValidatorsBasedOnCondition(
            this.prFormService
              .isTargetControlValueEqualTo('subjectToIfi', val => val == true)
              .bind(this),
            Validators.required,
          ),
        ],
      ],
      amount: [
        parameters?.patrimonyRepartition?.amount || 0,
        [Validators.required, Validators.min(1)],
      ],
      initialAmount: [
        parameters?.patrimonyRepartition?.initialAmount || 0,
        Validators.required,
      ],
      financialAssets: this.formBuilder.group({
        percent: [
          this.floored(
            parameters?.patrimonyRepartition?.financialAssets?.percent * 100,
          ) || 0,
        ],
        initialPercent: [
          parameters?.patrimonyRepartition?.financialAssets?.initialPercent ||
            0,
        ],
        amount: [
          parameters?.patrimonyRepartition?.financialAssets?.amount || 0,
        ],
      }),
      liquidity: this.formBuilder.group({
        percent: [
          this.floored(
            parameters?.patrimonyRepartition?.liquidity?.percent * 100,
          ) || 0,
        ],
        initialPercent: [
          parameters?.patrimonyRepartition?.liquidity?.initialPercent || 0,
        ],
        amount: [parameters?.patrimonyRepartition?.liquidity?.amount || 0],
      }),
      professionalAssets: this.formBuilder.group({
        percent: [
          this.floored(
            parameters?.patrimonyRepartition?.professionalAssets?.percent * 100,
          ) || 0,
        ],
        initialPercent: [
          parameters?.patrimonyRepartition?.professionalAssets
            ?.initialPercent || 0,
        ],
        amount: [
          parameters?.patrimonyRepartition?.professionalAssets?.amount || 0,
        ],
      }),
      realEstateInvestment: this.formBuilder.group({
        percent: [
          this.floored(
            parameters?.patrimonyRepartition?.realEstateInvestment?.percent *
              100,
          ) || 0,
        ],
        initialPercent: [
          parameters?.patrimonyRepartition?.realEstateInvestment
            ?.initialPercent || 0,
        ],
        amount: [
          parameters?.patrimonyRepartition?.realEstateInvestment?.amount || 0,
        ],
      }),
      realEstateUse: this.formBuilder.group({
        percent: [
          this.floored(
            parameters?.patrimonyRepartition?.realEstateUse?.percent * 100,
          ) || 0,
        ],
        initialPercent: [
          parameters?.patrimonyRepartition?.realEstateUse?.initialPercent || 0,
        ],
        amount: [parameters?.patrimonyRepartition?.realEstateUse?.amount || 0],
      }),

      amountFinancial: [parameters?.patrimonyRepartition?.amountFinancial || 0],
      amountNoFinancial: [
        parameters?.patrimonyRepartition?.amountNoFinancial || 0,
      ],
      passifTotal: [parameters?.patrimonyRepartition?.passifTotal || 0],
      amountRealEstateScpi: [
        parameters?.patrimonyRepartition?.amountRealEstateScpi || 0,
      ],
      amountRealEstateWithoutScpi: [
        parameters?.patrimonyRepartition?.amountRealEstateWithoutScpi || 0,
      ],
      amountRealEstateBrut: [
        parameters?.patrimonyRepartition?.amountRealEstateBrut || 0,
      ],
      amountRealEstateNet: [
        parameters?.patrimonyRepartition?.amountRealEstateNet || 0,
      ],
    };
    return this.formBuilder.group(formConfig, options);
  }

  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }
  populate(data: DistributionAssets) {
    let formArray = this.formBuilder.array([this.formGroup()]);
    if (data) {
      formArray.clear();
      data.distributionAssets.forEach((item, index) => {
        formArray.push(
          this.formBuilder.group({
            fundOrigin: [item?.fundOrigin, Validators.required],
            distribution: [+item.distribution * 100, Validators.required],
          }),
        );
      });
    }
    return formArray;
  }
  formGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      fundOrigin: ['LIQUIDITY', Validators.required],
      distribution: [100, Validators.required],
    });
  }
  extract() {
    const formValue = _.cloneDeep(this.form.value);
    const patrimonyDistribution: string[] = [];
    const distribution = {
      financialAssets: percent =>
        percent > 40 && patrimonyDistribution.push('FINANCIAL_ASSETS'),
      liquidity: percent => percent && patrimonyDistribution.push('LIQUIDITY'),
      professionalAssets: percent =>
        percent && patrimonyDistribution.push('PROFESSIONAL_ASSETS'),
      realEstateInvestment: percent =>
        percent > 40 && patrimonyDistribution.push('REAL_ESTATE_INVESTMENT'),
      realEstateUse: percent =>
        percent && patrimonyDistribution.push('REAL_ESTATE_PERSONAL'),
    };
    const amount = formValue.amount;
    for (const key in formValue) {
      if (Object.prototype.hasOwnProperty.call(formValue, key)) {
        const element = formValue[key] as any;

        distribution[key] && distribution[key](element.percent);

        if (typeof element === 'object' && element !== null) {
          element.percent = element.percent / 100;
          element.amount = amount * element.percent;
        }
      }
    }
    const pledge = this.subscriptionFacade.getPledge();
    if (pledge?.owners?.[0])
      pledge.owners[0].riskProfil.patrimonyDistribution = [
        ...patrimonyDistribution,
      ];

    this.subscriptionFacade.setPledge(pledge);

    return {
      ifi: formValue.ifi,
      subjectToIfi: formValue.subjectToIfi,

      patrimonyRepartition: { ..._.omit(formValue, ['ifi', 'subjectToIfi']) },
    };
  }
  floored(value) {
    if (value) return Math.round(value * 100) / 100;
  }
}
